import { useCallback, useEffect, useMemo, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { buildRoomsOverlay, findRoomAtPoint } from "../../utils/rooms";
import { isPointInPolygon } from "../../utils/selectionAreas";
import { ACTIVE_PAGE_INDEX } from "./constants";
import type { FloorPlanRoom, SelectionAreaPoint } from "../../utils/types";
import type { UseSelectedRoom } from "./types";

export const useSelectedRoom: UseSelectedRoom = ({
  rooms,
  isEnabled,
  resetKey,
  floorPlanPanelHandlers,
  onRoomChange,
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  const pageRooms = useMemo(
    () => rooms.filter((room) => room.pageIndex === ACTIVE_PAGE_INDEX),
    [rooms],
  );

  const selectedRoom = useMemo(
    () => pageRooms.find((room) => room.id === selectedRoomId) ?? null,
    [pageRooms, selectedRoomId],
  );

  const scopeRooms = useMemo(
    () => (selectedRoom ? [selectedRoom] : rooms),
    [selectedRoom, rooms],
  );

  const isRoomsLevel =
    isEnabled && pageRooms.length > 0 && selectedRoom === null;

  // Names on the rooms level, the entered room highlighted and unnamed inside it
  const overlay = useMemo(
    () =>
      buildRoomsOverlay({
        rooms: pageRooms,
        selectedRoomId: selectedRoom?.id ?? null,
        isInteractive: isRoomsLevel,
      }),
    [pageRooms, selectedRoom, isRoomsLevel],
  );

  const enterRoom = useCallback(
    (room: FloorPlanRoom) => {
      if (room.id !== selectedRoomId) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOM_SELECTED);
      }
      setSelectedRoomId(room.id);
      onRoomChange?.();
      // The padded fit; the element zoom would leave a room small in the middle
      floorPlanPanelHandlers?.current?.zoomToElements({
        elements: room.points,
      });
    },
    [selectedRoomId, onRoomChange, floorPlanPanelHandlers],
  );

  // Nothing to leave on the rooms level, so tab and mode switches do not move the map
  const leaveRoom = useCallback(() => {
    if (!selectedRoom) return;
    setSelectedRoomId(null);
    onRoomChange?.();
    floorPlanPanelHandlers?.current?.zoomToFit();
  }, [selectedRoom, onRoomChange, floorPlanPanelHandlers]);

  const selectRoom = useCallback(
    (roomId: number) => {
      const room = pageRooms.find((candidate) => candidate.id === roomId);
      if (room) enterRoom(room);
    },
    [pageRooms, enterRoom],
  );

  const handleRoomPickClick = useCallback(
    (pos: SelectionAreaPoint) => {
      const pickedRoom = findRoomAtPoint({ rooms: pageRooms, point: pos });
      if (pickedRoom) enterRoom(pickedRoom);
    },
    [pageRooms, enterRoom],
  );

  const handleOutsideClick = useCallback(
    (pos: SelectionAreaPoint) => {
      if (!selectedRoom) return;
      if (isPointInPolygon({ point: pos, ring: selectedRoom.points })) return;
      leaveRoom();
    },
    [selectedRoom, leaveRoom],
  );

  useEffect(() => {
    if (!isEnabled) setSelectedRoomId(null);
  }, [isEnabled]);

  useEffect(() => {
    setSelectedRoomId(null);
  }, [resetKey]);

  useEffect(() => {
    if (selectedRoomId === null) return;
    if (pageRooms.some((room) => room.id === selectedRoomId)) return;
    setSelectedRoomId(null);
  }, [pageRooms, selectedRoomId]);

  return {
    pageRooms,
    selectedRoom,
    scopeRooms,
    isRoomsLevel,
    overlay,
    selectRoom,
    clearRoomSelection: leaveRoom,
    handleRoomPickClick,
    handleOutsideClick,
  };
};
